import angular from 'angular';
import messages from 'angular-messages';
import sanitize from 'angular-sanitize';
import vsRepeat from 'angular-vs-repeat';

import i18n from '../i18n';
import uuid from '../uuid';
import indeterminateProgress from '../indeterminate-progress';

import akamList from './list-directive';

/**
 * @ngdoc module
 * @name akamai.components.list
 *
 * @guideline https://design.akamai.com/components/list/
 *
 * @requires module:ngModel
 * @requires module:ngSanitize
 * @requires module:ngMessages
 *
 * @description
 * The list component is used to display large amounts of data with a constant performance cost.
 * This is accomplished by using a virtual scroll. Item content can include text, numbers, links,
 * buttons, and images.
 *
 * @example list.html
 * {% raw %}
 * <akam-list ng-model="vm.model"
 *            items="vm.arrayOrPromise">
 *   {{ item.someProperty }}
 * </akam-list>
 * {% endraw %}
 */
export default angular.module('akamai.components.list', [
  sanitize,
  messages,
  vsRepeat,
  i18n.name,
  uuid.name,
  indeterminateProgress.name
])

/**
 * @ngdoc directive
 * @name akamList
 * @description Creates a list control
 *
 * @restrict E
 *
 * @param {Object[]|Promise} items Required data model for the list. Will display a loading
 * indicator if it is a promise.
 *
 * @param {string} [id-property=id] Used to determine object equality. An id must be unique per row.
 *
 * @param {*} [not-filterable] The presence of this attribute will remove the filter box
 *
 * @param {*} [not-selectable] The presence of this attribute will remove the selection
 * functionality
 *
 * @param {*} [striped] The presence of this attribute will render the table as striped. By default
 * all background colors will be white.
 *
 * @param {*} [sortable] The presence of this attribute will render a sortable icon in the toolbar
 * which will reverse the order of the list items when clicked.
 *
 * @param {string} [text-property] A shorthand method that can be used if custom markup is not
 * necessary. If provided, will be used for rendering and filtering.
 *
 * @param {Boolean} [is-disabled] Whether or not this control is disabled.
 *
 * @param {string|TranslateKey} [filter-placeholder] Text for the placeholder attribute on the
 * filter bar.
 *
 * @param {string|TranslateKey} [no-filter-results-message] Text that should display when a filter
 * returns zero results.
 *
 * @param {string|TranslateKey} [no-items-message] Text that should display when there are no items
 * to display.
 *
 * @param {string|TranslateKey} [loading-message] Text that should display under the loading
 * indicator if the `items` attribute is a promise.
 *
 * @param {string|TranslateKey} [failure-message] Text that should display if `items` is a promise
 * and it fails to load.
 *
 * @param {function(Object, string) : boolean} [on-filter] Callback that is invoked whenever a
 * filter takes place. This is required if custom markup is used. Will be passed an individual
 * item and the search term. Must return true if the item should be included in the result set
 * and false if not.
 */
  .directive('akamList', akamList);